import type { tLocale } from "./env";
import type { tMoney } from "./utils";
import type { tGeoPoint } from "./geo";

export enum eTaskSafedealProvider {
  TINKOFF_MARKETPLACE = "tinkoff_marketplace",
  TINKOFF_INVIDIUALS = "tinkoff_individuals",
  TEST = "test",
}

export type tTaskStatus =
  | "PENDING"
  | "ACCEPTED"
  | "DONE"
  | "CANCELLED"
  | "PAID"
  | "DISPUTE"
  | "DISPUTE_APPROVED"
  | "DISPUTE_REJECTED"
  | "PERSONAL"
  | "PAY_WAITING";

export type tTaskPayoutStatus = "WAITING" | "PROCESSING" | "DONE" | "ERROR";
export type tTaskRefundStatus =
  | "PROCESSING"
  | "CLIENT_REFUND_DONE"
  | "MASTER_PAYOUT_DONE"
  | "DONE";

export enum eTaskCancelReason {
  NOT_ACCEPTED,
  NOT_PAID,
  CANCELLED_BY_CLIENT,
  CANCELLED_BY_MASTER,
  CANCELLED_BY_SALOON,
}
export type tTaskLocation = "saloon" | "client" | "master" | "online";

export type tTaskAutoNotificationEvent =
  | "m_accept_task"
  | "c_cancel_task"
  | "m_cancel_task"
  | "m_create_task"
  | "c_create_task"
  | "c_change_task"
  | "m_change_task"
  | "new_disc"
  | "notify_m"
  | "notify_m_personal"
  | "notify_c";

export type tTaskParticipantMeta = {
  _id: string;
  first_name: string;
  phone?: string;
  email?: string;
  name: string;
  avatar?: string;
  sex: boolean;
};
export type tTaskCustomField = {
  key: string;
  value: string;
};
export type tTaskServiceMeta = {
  service: string;
  title: string;
  amount?: number;
  initial_price: tMoney;
  final_price: tMoney;
  discount?: tMoney;
  duration: number;
};

export type tTaskPrepaymentMethod = "cash" | "cashless";
export interface tTaskPrepaymentMeta {
  amount: tMoney;
  payment_method: tTaskPrepaymentMethod;
}

type tAbstractTask = {
  _id: string;
  locale: tLocale;
  master: string;
  status: tTaskStatus;
  custom_fields?: tTaskCustomField[];
  start_date: string;
  timezone?: string;
  end_date: string;
  auto_notifications?: tTaskAutoNotificationEvent[];
  /**
   * YYYY-MM-DD HH:mm in UTC
   */
  notify: string;
  /**
   * has reminder been sent?
   */
  notified: boolean;
  cancel_reason?: string;
  /**
   * server defined cancel reason
   */
  real_cancel_reason?: eTaskCancelReason;
  custom: boolean;
  personal?: boolean;
  location?: tGeoPoint;
  /**
   * calendar color
   */
  cal_color?: string;
  master_note?: string;
  created_at: string;
};
export type tBusinessTask = tAbstractTask & {
  personal?: false;
  location_type: tTaskLocation;
  client?: string;
  /**
   * id of the real user who is the client of this task
   */
  consumer?: string;
  saloon?: string;
  review?: string;
  safe: boolean;
  safe_provider?: eTaskSafedealProvider;
  safe_provider_currency?: string;
  safe_provider_deal_id?: string;
  safe_service_fee?: number;
  safe_service_fee_perc?: number;
  safe_price_without_fee?: number;
  safe_price?: number;
  refund_status?: tTaskRefundStatus;
  payout_status?: tTaskPayoutStatus;
  pay_id?: number;
  payout_id?: string;
  mp_shop_id?: string;
  created_by_anon_client?: boolean;
  is_new_client?: boolean;
  services: tTaskServiceMeta[];
  initial_price: tMoney;
  price: tMoney;
  prepayment?: Maybe<tTaskPrepaymentMeta>;
  master_commission: tMoney;
  /**
   * note of the task's consumer
   */
  client_note?: string;
  photos: string[];
  accept_deadline?: string;
  pay_deadline?: string;
  dispute_deadline?: string;
  claim?: string;
  /**
   * should hide the task's client from the master?
   */
  hide_client?: boolean;
  /**
   * message from the anonymous client who don't have the account
   */
  anon_client_msg?: string;
  /**
   * message to the client from the master (available only while creating the task)
   */
  msg_for_client?: string;
  master_requested_photo: boolean;
  client_requested_photo?: boolean;
  client_meta: tTaskParticipantMeta;
  master_meta: tTaskParticipantMeta;
  /**
   * was task auto accepted?
   */
  auto_accept?: boolean;
  master_requested_review?: boolean;
};

export type tPersonalTask = tAbstractTask & {
  custom: true;
  personal: true;
  title?: string;
  personal_photos?: string;
};

export type tAnyTask = tBusinessTask | tPersonalTask;

export interface iRequestTaskPayoutDto {
  tinkoffIndividuals?: {
    card_id?: string;
    spb_phone?: string;
    spb_member_id?: string;
  };
}
